// src/components/AttendanceTypesPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

import { db } from '../firebase';
import {
    collection, query, where, getDocs, orderBy, deleteDoc, doc, addDoc, updateDoc, Timestamp
} from 'firebase/firestore';

// Import Font Awesome Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPlus, faTrashAlt, faSpinner, faEdit, faTags, faLightbulb } from '@fortawesome/free-solid-svg-icons';

// Import react-bootstrap components
import { Alert, Modal, Button, Form } from 'react-bootstrap';

// Import the Auth Context hook
import { useAuth } from '../context/AuthContext';

import './AttendanceTypesPage.css';

// Suggested event types
const SUGGESTED_TYPES = [
    { name: 'Sunday Service', description: 'Sunday worship service' },
    { name: 'Saturday Service', description: 'Saturday worship service' },
    { name: 'Wednesday Service', description: 'Midweek service' },
    { name: 'Youth Group', description: 'Youth ministry gathering' },
    { name: 'Small Group', description: 'Small group or Bible study' },
    { name: 'Special Event', description: 'Special events and celebrations' },
    { name: 'Prayer Meeting', description: 'Dedicated prayer time' },
    { name: 'Mens Ministry', description: 'Mens group gathering' },
    { name: 'Womens Ministry', description: 'Womens group gathering' }
];

// --- Main Component: AttendanceTypesPage ---
function AttendanceTypesPage() {
    const { currentUser, isAdmin, userOrgId, loadingAuth } = useAuth();

    // Local state
    const [eventTypes, setEventTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Delete modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [typeToDelete, setTypeToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Add/Edit modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingType, setEditingType] = useState(null);
    const [typeForm, setTypeForm] = useState({ name: '', description: '' });
    const [isSaving, setIsSaving] = useState(false);

    // --- Fetch Event Types ---
    const fetchEventTypes = useCallback(async () => {
        if (!userOrgId) {
            if (!loadingAuth && currentUser) {
                setError("Organization details missing from your account.");
            }
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);

        try {
            const typesQuery = query(
                collection(db, 'attendanceType'),
                where('orgId', '==', userOrgId),
                orderBy('name', 'asc')
            );
            const typesSnap = await getDocs(typesQuery);

            const fetchedTypes = typesSnap.docs.map(d => ({
                ...d.data(),
                id: d.id
            }));

            setEventTypes(fetchedTypes);
            setError(null);
        } catch (err) {
            console.error("AttendanceTypesPage: Error fetching types", err);
            setError(err.message || "Failed to load event types.");
        } finally {
            setLoading(false);
        }
    }, [userOrgId, loadingAuth, currentUser]);

    useEffect(() => {
        if (!loadingAuth && userOrgId) {
            fetchEventTypes();
        } else if (!loadingAuth && !userOrgId && currentUser) {
            setError("Organization details missing from your account.");
            setLoading(false);
        } else if (!loadingAuth && !currentUser) {
            setEventTypes([]);
            setLoading(false);
            setError(null);
        }
    }, [loadingAuth, userOrgId, fetchEventTypes, currentUser]);

    // Get suggestions that haven't been added yet
    const availableSuggestions = SUGGESTED_TYPES.filter(
        suggestion => !eventTypes.some(type => type.name.toLowerCase() === suggestion.name.toLowerCase())
    );

    // --- Delete Handler ---
    const handleDeleteClick = (type) => {
        setTypeToDelete(type);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!typeToDelete || !userOrgId || !isAdmin) return;

        setIsDeleting(true);
        try {
            await deleteDoc(doc(db, 'attendanceType', typeToDelete.id));
            setEventTypes(prev => prev.filter(t => t.id !== typeToDelete.id));
            setShowDeleteModal(false);
            setTypeToDelete(null);
        } catch (err) {
            console.error("Error deleting type:", err);
            setError("Failed to delete type: " + err.message);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCloseDeleteModal = () => {
        if (!isDeleting) {
            setShowDeleteModal(false);
            setTypeToDelete(null);
        }
    };

    // --- Add/Edit Handlers ---
    const handleOpenAddModal = () => {
        setEditingType(null);
        setTypeForm({ name: '', description: '' });
        setShowEditModal(true);
    };

    const handleOpenEditModal = (type) => {
        setEditingType(type);
        setTypeForm({ name: type.name, description: type.description || '' });
        setShowEditModal(true);
    };

    const handleQuickAdd = async (suggestion) => {
        setIsSaving(true);
        try {
            const docRef = await addDoc(collection(db, 'attendanceType'), {
                orgId: userOrgId,
                name: suggestion.name,
                description: suggestion.description,
                createdAt: Timestamp.now(),
                createdBy: currentUser.uid
            });

            const newType = {
                id: docRef.id,
                orgId: userOrgId,
                name: suggestion.name,
                description: suggestion.description,
                createdAt: Timestamp.now(),
                createdBy: currentUser.uid
            };

            setEventTypes(prev => [...prev, newType].sort((a, b) => a.name.localeCompare(b.name)));
        } catch (err) {
            console.error("Error adding type:", err);
            setError("Failed to add type: " + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveType = async () => {
        if (!typeForm.name.trim()) {
            setError("Please enter a type name.");
            return;
        }

        setIsSaving(true);
        try {
            if (editingType) {
                // Update existing
                await updateDoc(doc(db, 'attendanceType', editingType.id), {
                    name: typeForm.name.trim(),
                    description: typeForm.description.trim()
                });

                setEventTypes(prev => prev.map(t =>
                    t.id === editingType.id
                        ? { ...t, name: typeForm.name.trim(), description: typeForm.description.trim() }
                        : t
                ).sort((a, b) => a.name.localeCompare(b.name)));
            } else {
                // Add new
                const docRef = await addDoc(collection(db, 'attendanceType'), {
                    orgId: userOrgId,
                    name: typeForm.name.trim(),
                    description: typeForm.description.trim(),
                    createdAt: Timestamp.now(),
                    createdBy: currentUser.uid
                });

                const newType = {
                    id: docRef.id,
                    orgId: userOrgId,
                    name: typeForm.name.trim(),
                    description: typeForm.description.trim(),
                    createdAt: Timestamp.now(),
                    createdBy: currentUser.uid
                };

                setEventTypes(prev => [...prev, newType].sort((a, b) => a.name.localeCompare(b.name)));
            }

            setShowEditModal(false);
            setEditingType(null);
            setTypeForm({ name: '', description: '' });
        } catch (err) {
            console.error("Error saving type:", err);
            setError("Failed to save type: " + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCloseEditModal = () => {
        if (!isSaving) {
            setShowEditModal(false);
            setEditingType(null);
            setTypeForm({ name: '', description: '' });
        }
    };

    // --- Render Logic ---

    // Loading state
    if (loadingAuth || loading) {
        return (
            <div className="attendance-types-page">
                <div className="attendance-types-loading">
                    <div className="skeleton-header"></div>
                    <div className="skeleton-type-card"></div>
                    <div className="skeleton-type-card"></div>
                    <div className="skeleton-type-card"></div>
                </div>
            </div>
        );
    }

    // Not logged in
    if (!currentUser) {
        return (
            <div className="attendance-types-page">
                <div className="container mt-3">
                    <Alert variant="warning">
                        Please <Alert.Link as={Link} to="/login">log in</Alert.Link> to manage event types.
                    </Alert>
                </div>
            </div>
        );
    }

    // Not admin
    if (!isAdmin) {
        return (
            <div className="attendance-types-page">
                <div className="attendance-types-header">
                    <div className="container">
                        <div className="attendance-types-header-content">
                            <div className="attendance-types-header-left">
                                <Link to="/attendance" className="form-back-btn">
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </Link>
                                <div>
                                    <h1 className="attendance-types-title">Event Types</h1>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <div className="attendance-types-admin-only">
                        <div className="attendance-types-admin-only-icon">
                            <FontAwesomeIcon icon={faTags} />
                        </div>
                        <h3>Admin Access Required</h3>
                        <p>Managing event types is only available to organization administrators.</p>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="attendance-types-page">
                <div className="attendance-types-header">
                    <div className="container">
                        <div className="attendance-types-header-content">
                            <div className="attendance-types-header-left">
                                <Link to="/attendance" className="form-back-btn">
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </Link>
                                <div>
                                    <h1 className="attendance-types-title">Event Types</h1>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <Alert variant="danger" onClose={() => setError(null)} dismissible>
                        Error: {error}
                    </Alert>
                </div>
            </div>
        );
    }

    return (
        <div className="attendance-types-page">
            {/* Page Header */}
            <div className="attendance-types-header">
                <div className="container">
                    <div className="attendance-types-header-content">
                        <div className="attendance-types-header-left">
                            <Link to="/attendance" className="form-back-btn">
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </Link>
                            <div>
                                <h1 className="attendance-types-title">Event Types</h1>
                                {eventTypes.length > 0 && (
                                    <div className="attendance-types-count">
                                        {eventTypes.length} type{eventTypes.length !== 1 ? 's' : ''}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">
                {/* Existing Types */}
                <div className="types-section">
                    <h3 className="types-section-title">Your Event Types</h3>

                    {eventTypes.length === 0 && (
                        <div className="types-empty">
                            <div className="types-empty-icon">
                                <FontAwesomeIcon icon={faTags} />
                            </div>
                            <h4>No Event Types</h4>
                            <p>Add event types to start tracking attendance.</p>
                        </div>
                    )}

                    {eventTypes.map(type => (
                        <div key={type.id} className="type-card">
                            <div className="type-content">
                                <div className="type-name">{type.name}</div>
                                {type.description && (
                                    <div className="type-description">{type.description}</div>
                                )}
                            </div>
                            <div className="type-actions">
                                <button
                                    className="type-action-btn type-action-btn-edit"
                                    onClick={() => handleOpenEditModal(type)}
                                    title="Edit Type"
                                >
                                    <FontAwesomeIcon icon={faEdit} />
                                </button>
                                <button
                                    className="type-action-btn type-action-btn-delete"
                                    onClick={() => handleDeleteClick(type)}
                                    title="Delete Type"
                                >
                                    <FontAwesomeIcon icon={faTrashAlt} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Suggested Types */}
                {availableSuggestions.length > 0 && (
                    <div className="types-section suggestions-section">
                        <h3 className="types-section-title">
                            <FontAwesomeIcon icon={faLightbulb} className="suggestion-icon" />
                            Suggested Types
                        </h3>
                        <p className="suggestions-description">
                            Quick-add common event types for your organization.
                        </p>
                        <div className="suggestions-grid">
                            {availableSuggestions.map((suggestion, index) => (
                                <button
                                    key={index}
                                    className="suggestion-chip"
                                    onClick={() => handleQuickAdd(suggestion)}
                                    disabled={isSaving}
                                >
                                    <FontAwesomeIcon icon={faPlus} className="suggestion-chip-icon" />
                                    {suggestion.name}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* FAB Button */}
            <button className="fab-add-type" onClick={handleOpenAddModal}>
                <FontAwesomeIcon icon={faPlus} />
                <span>Add Type</span>
            </button>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered className="types-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Delete Event Type</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete <strong>"{typeToDelete?.name}"</strong>?</p>
                    <p className="text-muted mb-0">
                        This will not delete existing attendance records using this type.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleConfirmDelete} disabled={isDeleting}>
                        {isDeleting ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                Deleting...
                            </>
                        ) : (
                            "Delete Type"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Add/Edit Modal */}
            <Modal show={showEditModal} onHide={handleCloseEditModal} centered className="types-modal">
                <Modal.Header closeButton>
                    <Modal.Title>{editingType ? 'Edit Event Type' : 'Add Event Type'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Type Name <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g., Sunday Service"
                                value={typeForm.name}
                                onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                placeholder="Optional description"
                                value={typeForm.description}
                                onChange={(e) => setTypeForm({ ...typeForm, description: e.target.value })}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseEditModal} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSaveType} disabled={isSaving}>
                        {isSaving ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                Saving...
                            </>
                        ) : (
                            editingType ? "Save Changes" : "Add Type"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default AttendanceTypesPage;
